/**
 * Iter 246 · Lightweight TipTap rich-text editor for public (unauthenticated)
 * pages. Deliberately does NOT import `@/lib/api` — SmartRichText's slash-
 * command AI helpers would 401 on the public client portal.
 *
 * Supported formatting: bold, italic, unordered list, ordered list, link.
 * Output is HTML that the backend sanitises via a strict tag allow-list.
 */
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { Bold, Italic, List, ListOrdered, Link as LinkIcon } from "lucide-react";

export default function PortalRichText({ value, onChange, placeholder = "Write your note…", testid = "portal-rte" }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        codeBlock: false,
        horizontalRule: false,
      }),
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: "noopener noreferrer nofollow", target: "_blank" } }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      onChange?.(html === "<p></p>" ? "" : html);
    },
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none focus:outline-none min-h-[92px] px-3 py-2 text-sm",
        "data-testid": `${testid}-editor`,
      },
    },
  });

  if (!editor) return null;

  const btn = (active, onClick, Icon, tid, title) => (
    <button
      type="button"
      onClick={onClick}
      className={`w-7 h-7 flex items-center justify-center border border-slate-200 hover:bg-slate-100 ${active ? "bg-slate-200 text-slate-900" : "text-slate-600"}`}
      title={title}
      data-testid={tid}
    >
      <Icon className="w-3.5 h-3.5" />
    </button>
  );

  const promptLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("URL", prev || "https://");
    if (url === null) return;
    if (!url) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    if (!/^https?:\/\//i.test(url) && !url.startsWith("mailto:")) return;
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className="border border-slate-300 bg-white rounded" data-testid={testid}>
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-slate-200 bg-slate-50">
        {btn(editor.isActive("bold"), () => editor.chain().focus().toggleBold().run(), Bold, `${testid}-bold`, "Bold")}
        {btn(editor.isActive("italic"), () => editor.chain().focus().toggleItalic().run(), Italic, `${testid}-italic`, "Italic")}
        {btn(editor.isActive("bulletList"), () => editor.chain().focus().toggleBulletList().run(), List, `${testid}-ul`, "Bulleted list")}
        {btn(editor.isActive("orderedList"), () => editor.chain().focus().toggleOrderedList().run(), ListOrdered, `${testid}-ol`, "Numbered list")}
        {btn(editor.isActive("link"), promptLink, LinkIcon, `${testid}-link`, "Add link")}
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
